import React, { useEffect, useRef, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import * as styles from "./UserEngagement.module.scss";
import { MdClose } from "react-icons/md";
import { MantineProvider, MultiSelect, Select, TextInput } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ButtonComponent from "../Home/ButtonComponent/ButtonComponent";
import { ndeFormInstance } from "../../lib/axios-client";
import posthog from "posthog-js";
import { navigate } from "gatsby";
import { toast } from "react-toastify";

const schema = yup.object({
  name: yup
    .string()
    .required("This field is required!")
    .transform((value) => value.trim())
    .min(3, "Name must contain at least 3 letters!"),
  email: yup
    .string()
    .required("This field is required!")
    .transform((value) => value.trim())
    .email("Please enter a valid email!")
    .test(
      "no-consecutive-dots",
      "Email cannot contain consecutive dots. Please enter a valid email.",
      (value) => !value || !/\.\./.test(value)
    ),
  service_items: yup.array().of(yup.string()).optional(),
});

export default function UserEngagement() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [opened, { open, close }] = useDisclosure();
  const [apiLoading, setApiLoading] = useState(false);

  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);

  const triggerModal = () => {
    const hasShown = sessionStorage.getItem("contact_form");
    if (!hasShown) {
      open();
      sessionStorage.setItem("contact_form", "true");
    }
  };

  const resetInactivityTimer = () => {
    const hasShown = sessionStorage.getItem("contact_form");
    if (hasShown) return;

    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

    inactivityTimer.current = setTimeout(() => {
      triggerModal();
    }, 15000);
  };

  const onSubmit = async (data) => {
    setApiLoading(true);

    try {
      const landing_page = sessionStorage.getItem("landing_page");
      await ndeFormInstance.post("/lead-tracking", {
        ...data,
        form_id: 'close-intent-popup',
        landing_page,
      });
      if ((window as any).gtag) {
        (window as any).gtag("event", "contact_form_submitted");
      }
      posthog.capture("form_submitted", {
        form_id: "contact_form_submitted",
        submission_status: "success",
      });
      close();
      navigate("/thank-you");
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    const events = ["mousemove", "click", "scroll", "keydown"];
    events.forEach((event) =>
      document.addEventListener(event, resetInactivityTimer)
    );

    resetInactivityTimer();

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, resetInactivityTimer)
      );
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  useEffect(() => {
    const handleMouseOut = (e: MouseEvent) => {
      const hasShown = sessionStorage.getItem("contact_form");
      if (hasShown) return;

      if (!e.relatedTarget) {
        triggerModal();
      }
    };

    window.addEventListener("mouseout", handleMouseOut);

    return () => {
      window.removeEventListener("mouseout", handleMouseOut);
    };
  }, []);

  return (
    <MantineProvider>
      {opened && (
        <div className={styles.UserEngagement__backdrop}>
          <div className={styles.UserEngagement__container}>
            <button
              className={styles.UserEngagement__closeButton}
              onClick={close}
            >
              Close <MdClose size={20} />
            </button>
            <div className={styles.UserEngagement__modal}>
              <div className={styles.UserEngagement__heading}>
                Your next big idea deserves better design, talk to us!!
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.UserEngagement__formContainer}
              >
                <TextInput
                  placeholder="What is your name? *"
                  size="lg"
                  className={styles.UserEngagement__fieldArea}
                  {...register("name")}
                  error={errors.name?.message}
                />

                <TextInput
                  placeholder="Your email for contact *"
                  size="lg"
                  className={styles.UserEngagement__fieldArea}
                  {...register("email")}
                  error={errors.email?.message}
                />

                <Controller
                  control={control}
                  name="service_items"
                  render={({ field }) => (
                    <MultiSelect
                      data={[
                        "Website Design",
                        "Mobile App Design",
                        "SaaS Product Design",
                        "eCommerce",
                        "UI/UX Audit",
                        "Other",
                      ]}
                      placeholder="Project Type"
                      size="lg"
                      classNames={{
                        input: styles.inputField,
                        option: styles.multiValue,
                        pill: styles.pills,
                      }}
                      onChange={field.onChange}
                      error={errors.service_items?.message}
                    />
                  )}
                />

                <ButtonComponent
                  buttonText={"Submit"}
                  buttonStyle={"solidBg"}
                  type="submit"
                  className={styles.UserEngagement__submit_button}
                  onClick={undefined}
                  disabled={undefined}
                  loading={apiLoading}
                />
              </form>
            </div>
          </div>
        </div>
      )}
    </MantineProvider>
  );
}
